/**
 * Pure game logic for the minigame demos, ported verbatim from the mobile
 * renderers so the web previews behave identically to what participants play.
 * Kept side-effect-free (no React, no DOM) so each rule is unit-testable.
 *
 * Sources:
 * - memory: apps/hunts/mobile/.../minigames/MemoryMatchSubmissionForm.tsx
 * - puzzle: apps/hunts/mobile/.../challenges/SlidingPuzzle.tsx
 * - slot/related/guess-word: the respective mobile *SubmissionForm.tsx files
 */

/** Fisher–Yates shuffle. Returns a new array; never mutates the input. */
export function shuffle<T>(items: ReadonlyArray<T>): T[] {
  const out = items.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

// ---------------------------------------------------------------------------
// Memory match
// ---------------------------------------------------------------------------

export interface MemoryCard {
  /** Stable id for React keys + matched-set membership. */
  id: number;
  /** Index into the image bank — two cards share an imageIndex per pair. */
  imageIndex: number;
}

/**
 * Build a shuffled deck of `pairCount * 2` cards. Cycles the image bank if there
 * are fewer images than pairs so the renderer can't crash on under-config.
 */
export function buildDeck(pairCount: number, imageBankSize: number): MemoryCard[] {
  const bank = Math.max(1, imageBankSize);
  const indices: number[] = [];
  for (let i = 0; i < pairCount; i++) {
    indices.push(i % bank, i % bank);
  }
  return shuffle(indices).map((imageIndex, id) => ({ id, imageIndex }));
}

// ---------------------------------------------------------------------------
// Sliding puzzle
// ---------------------------------------------------------------------------

/** Count inversions (larger tile before a smaller one), excluding the blank. */
export function countInversions(tiles: number[], gridSize: number): number {
  const blank = gridSize * gridSize - 1;
  const filtered = tiles.filter((t) => t !== blank);
  let inversions = 0;
  for (let i = 0; i < filtered.length; i++) {
    for (let j = i + 1; j < filtered.length; j++) {
      if (filtered[i] > filtered[j]) inversions++;
    }
  }
  return inversions;
}

/**
 * Whether a permutation is solvable. Odd grids: even inversions. Even grids:
 * `inversions + blankRowFromBottom` is odd.
 */
export function isSolvable(tiles: number[], gridSize: number): boolean {
  const inversions = countInversions(tiles, gridSize);
  const blankIndex = tiles.indexOf(gridSize * gridSize - 1);
  const blankRowFromBottom = gridSize - 1 - Math.floor(blankIndex / gridSize);
  if (gridSize % 2 === 1) return inversions % 2 === 0;
  return (inversions + blankRowFromBottom) % 2 === 1;
}

/** Generate a solvable, not-already-solved shuffled puzzle. */
export function generateShuffledTiles(gridSize: number): number[] {
  const total = gridSize * gridSize;
  const tiles = shuffle(Array.from({ length: total }, (_, i) => i));

  if (!isSolvable(tiles, gridSize)) {
    // Swap two non-blank tiles to flip parity.
    const nonBlank = tiles.filter((t) => t !== total - 1);
    const i0 = tiles.indexOf(nonBlank[0]);
    const i1 = tiles.indexOf(nonBlank[1]);
    [tiles[i0], tiles[i1]] = [tiles[i1], tiles[i0]];
  }

  if (tiles.every((t, i) => t === i)) {
    // Avoid handing the player an already-solved board.
    const i0 = tiles.indexOf(0);
    const i1 = tiles.indexOf(1);
    [tiles[i0], tiles[i1]] = [tiles[i1], tiles[i0]];
  }

  return tiles;
}

/** Solved when every tile sits at its own index. */
export function isPuzzleSolved(tiles: number[]): boolean {
  return tiles.every((tile, index) => tile === index);
}

/** Indices of tiles adjacent to the blank (the only movable ones). */
export function getMovableTiles(tiles: number[], gridSize: number): Set<number> {
  const blankIndex = tiles.indexOf(gridSize * gridSize - 1);
  const row = Math.floor(blankIndex / gridSize);
  const col = blankIndex % gridSize;
  const movable = new Set<number>();
  if (row > 0) movable.add(blankIndex - gridSize);
  if (row < gridSize - 1) movable.add(blankIndex + gridSize);
  if (col > 0) movable.add(blankIndex - 1);
  if (col < gridSize - 1) movable.add(blankIndex + 1);
  return movable;
}

// ---------------------------------------------------------------------------
// Slot machine
// ---------------------------------------------------------------------------

/** The largest count of any single image across the landed reels. */
export function maxMatchCount(landed: ReadonlyArray<number>): number {
  if (landed.length === 0) return 0;
  const counts = new Map<number, number>();
  for (const idx of landed) counts.set(idx, (counts.get(idx) ?? 0) + 1);
  return Math.max(...counts.values());
}

// ---------------------------------------------------------------------------
// Related words
// ---------------------------------------------------------------------------

export interface WordTile {
  id: number;
  word: string;
  isCorrect: boolean;
}

/** Shuffle correct words + decoys into tagged tiles. */
export function buildWordTiles(words: string[], decoyWords: string[]): WordTile[] {
  const merged = [
    ...words.map((word) => ({ word, isCorrect: true })),
    ...decoyWords.map((word) => ({ word, isCorrect: false })),
  ];
  return shuffle(merged).map((tile, id) => ({ id, ...tile }));
}

/** Win when every correct tile is selected and no decoy is. */
export function isRelatedWordsWin(tiles: WordTile[], selected: ReadonlySet<number>): boolean {
  const allCorrectSelected = tiles.filter((t) => t.isCorrect).every((t) => selected.has(t.id));
  const noDecoysSelected = tiles.filter((t) => !t.isCorrect).every((t) => !selected.has(t.id));
  return allCorrectSelected && noDecoysSelected;
}

// ---------------------------------------------------------------------------
// Guess word
// ---------------------------------------------------------------------------

export type GuessWordState = 'playing' | 'won' | 'lost';

/** The distinct A–Z letters that make up a word (ignores spaces/punctuation). */
export function wordLetterSet(word: string): Set<string> {
  return new Set(
    word
      .toUpperCase()
      .split('')
      .filter((c) => /[A-Z]/.test(c)),
  );
}

/** Derive win/lose/playing + remaining lives from the picked letters. */
export function guessWordStatus(
  word: string,
  picked: ReadonlySet<string>,
  maxGuesses: number,
): { state: GuessWordState; remaining: number } {
  const letters = wordLetterSet(word);
  const wrong = Array.from(picked).filter((c) => !letters.has(c)).length;
  const remaining = Math.max(0, maxGuesses - wrong);
  const allRevealed = letters.size > 0 && Array.from(letters).every((c) => picked.has(c));
  const state: GuessWordState = allRevealed ? 'won' : remaining === 0 ? 'lost' : 'playing';
  return { state, remaining };
}
